import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useEffect, useRef } from 'react';

interface GuardProps {
  children: React.ReactNode;
}

/**
 * Strict guard: only platform admins ("admin" role) may pass.
 * Use for system-wide management surfaces.
 */
export function PlatformAdminRoute({ children }: GuardProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();
  const hasShownToast = useRef(false);
  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (!isLoading && isAuthenticated && !isAdmin && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error('Access denied. Admin privileges required.');
    }
  }, [isLoading, isAuthenticated, isAdmin]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!isAdmin) {
    // Network admins should not bounce to /dashboard — send them to their allowed admin home.
    if (hasRole('network_admin')) return <Navigate to="/admin/podcasts" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/**
 * Permissive guard: platform admins OR network admins may pass.
 * Use for surfaces that network admins can see (scoped by RLS): Podcasts, Approval Queue, Users.
 */
export function AnyAdminRoute({ children }: GuardProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();
  const hasShownToast = useRef(false);
  const isAnyAdmin = hasRole('admin') || hasRole('network_admin');

  useEffect(() => {
    if (!isLoading && isAuthenticated && !isAnyAdmin && !hasShownToast.current) {
      hasShownToast.current = true;
      toast.error('Access denied. Admin privileges required.');
    }
  }, [isLoading, isAuthenticated, isAnyAdmin]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  if (!isAnyAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * Backwards-compatible alias — equivalent to PlatformAdminRoute.
 * Existing imports of `AdminProtectedRoute` keep working.
 */
export const AdminProtectedRoute = PlatformAdminRoute;
